import React, { useState, useEffect } from 'react';
import type { MLAlgorithm } from './Algorithm';

interface MLConfigProps {
  algorithm: MLAlgorithm;
  saiFileId?: number;
  lastUsedConfig?: MLConfigState | null;
  onBack: () => void;
  onNext: (config: any) => void;
}

interface MLConfigState {
  // Common fields
  testTrainSplit: string;
  scaler: 'none' | 'standard scaler' | 'min-max scaler';
  // Random Forest / Gradient Boosting fields
  nEstimators?: string;
  maxDepth?: string;
  // Support Vector fields
  kernel?: 'rbf' | 'poly' | 'linear';
  degree?: string;
  c?: string;
  // Neural Network fields (for future use)
  numLayers?: string;
  numNodes?: string;
  epochs?: string;
  learningRate?: string;
}

interface InfoSubItem {
  title: string;
  description: string;
  subItems?: InfoSubItem[];
}

interface InfoItem {
  title: string;
  description: string;
  subItems?: InfoSubItem[];
}

const getInitialConfig = (algorithm: MLAlgorithm): MLConfigState => {
  const baseConfig: MLConfigState = {
    testTrainSplit: '15',
    scaler: 'standard scaler',
  };

  switch (algorithm) {
    case 'support vector':
      return {
        ...baseConfig,
        kernel: 'rbf',
        degree: '0',
        c: '0.5',
      };
    case 'gradient boosting':
    case 'random forest':
      return {
        ...baseConfig,
        nEstimators: '50',
        maxDepth: '50',
      };
    case 'neural network':
      return {
        ...baseConfig,
        numLayers: '100',
        numNodes: '512',
        epochs: '250',
        learningRate: '0.001',
      };
    default:
      return baseConfig;
  }
};

export const MLConfig: React.FC<MLConfigProps> = ({ algorithm, saiFileId, lastUsedConfig, onBack, onNext }) => {
  const [config, setConfig] = useState<MLConfigState>(() => {
    // Merge last used config with defaults for the current algorithm
    // This ensures all required fields exist even if switching algorithms
    const defaults = getInitialConfig(algorithm);
    if (lastUsedConfig) {
      return {
        ...defaults,
        ...lastUsedConfig,
      };
    }
    return defaults;
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Update config when algorithm changes
  useEffect(() => {
    // Merge lastUsedConfig with new algorithm defaults when algorithm changes
    const defaults = getInitialConfig(algorithm);
    if (lastUsedConfig) {
      setConfig({
        ...defaults,
        ...lastUsedConfig,
      });
    } else {
      setConfig(defaults);
    }
    setFieldErrors({});
    setError(null);
  }, [algorithm, lastUsedConfig]);

  const getInfoItems = (): InfoItem[] => {
    const commonItems: InfoItem[] = [
      {
        title: 'Test-train data split',
        description: 'Percentage of dataset to use for testing',
      },
      {
        title: 'Scaler',
        description: 'Used to scale the data',
        subItems: [
          {
            title: 'Standard Scaler',
            description: 'Standardize features by removing the mean and scaling to unit variance',
          },
          {
            title: 'Min-max scaler',
            description: 'Transform features to a range [0,1]',
          },
        ],
      },
    ];

    switch (algorithm) {
      case 'random forest':
        return [
          {
            title: 'N Estimators',
            description: 'The number of decision trees in the emodel',
          },
          {
            title: 'Max Depth',
            description: 'Maximum depth of each tree (can be None then the depth will be dynamically decided)',
          },
          ...commonItems,
        ];

      case 'support vector':
        return [
          {
            title: 'Kernel',
            description: 'Specifies the kernel type to be used to map input data into higher dimensions',
            subItems: [
              {
                title: 'Rbf',
                description: 'Radial basis function',
              },
              {
                title: 'Poly',
                description: 'Polynomial function of specified degree',
                subItems: [
                  {
                    title: 'Degree',
                    description: 'Degree of polynomial kernel, must be non-negative. Ignored by other kernels',
                  },
                ],
              },
              {
                title: 'Linear',
                description: 'line/hyperplane',
              },
            ],
          },
          {
            title: 'C',
            description: 'Regularization parameter to prevent overfitting. The strength of regularization is inversely proportional to C. Must be positive.',
          },
          ...commonItems,
        ];

      case 'neural network':
        return [
          {
            title: 'Number of layers',
            description: 'Number of layers in the neural network model',
          },
          {
            title: 'Number of nodes',
            description: 'Number of nodes in each layer',
          },
          {
            title: 'Epochs',
            description: 'Number of iterations to train the model for',
          },
          {
            title: 'Learning rate',
            description: 'Step size for updating model parameters at each epoch',
          },
          ...commonItems,
        ];

      case 'gradient boosting':
        return [
          {
            title: 'N Estimators',
            description: 'The number of boosting stages to perform',
          },
          {
            title: 'Max Depth',
            description: 'Maximum depth of the individual regression estimators',
          },
          {
            title: 'Learning Rate',
            description: 'Learning rate shrinks the contribution of each tree',
          },
          ...commonItems,
        ];

      default:
        return commonItems;
    }
  };

  const infoItems = getInfoItems();

  const renderSubItems = (subItems: InfoSubItem[], depth: number = 2): React.ReactElement => {
    return (
      <ul>
        {subItems.map((subItem, index) => (
          <li key={index}>
            <strong>{subItem.title}</strong>
            <p>{subItem.description}</p>
            {subItem.subItems && renderSubItems(subItem.subItems, depth + 1)}
          </li>
        ))}
      </ul>
    );
  };

  const getAlgorithmTitle = () => {
    switch (algorithm) {
      case 'random forest':
        return 'Random Forest';
      case 'support vector':
        return 'Support Vector Machine';
      case 'neural network':
        return 'Neural Network';
      case 'gradient boosting':
        return 'Gradient Boosting';
      default:
        return 'ML Algorithm';
    }
  };

  const handleInputChange = (field: keyof MLConfigState, value: string) => {
    setConfig({
      ...config,
      [field]: value,
    });
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors({
        ...fieldErrors,
        [field]: '',
      });
    }
  };

  const validateConfig = (): boolean => {
    const errors: Record<string, string> = {};

    // Common required fields
    if (!config.testTrainSplit || config.testTrainSplit.trim() === '') {
      errors.testTrainSplit = 'Test-train data split is required';
    }

    // Algorithm-specific validation
    switch (algorithm) {
      case 'support vector':
        if (!config.c || config.c.trim() === '') {
          errors.c = 'C is required';
        }
        if (config.kernel === 'poly' && (!config.degree || config.degree.trim() === '')) {
          errors.degree = 'Degree is required when using polynomial kernel';
        }
        break;

      case 'random forest':
      case 'gradient boosting':
        if (!config.nEstimators || config.nEstimators.trim() === '') {
          errors.nEstimators = 'N Estimators is required';
        }
        if (!config.maxDepth || config.maxDepth.trim() === '') {
          errors.maxDepth = 'Max Depth is required';
        }
        break;

      case 'neural network':
        if (!config.numLayers || config.numLayers.trim() === '') {
          errors.numLayers = 'Number of layers is required';
        }
        if (!config.numNodes || config.numNodes.trim() === '') {
          errors.numNodes = 'Number of nodes is required';
        }
        if (!config.epochs || config.epochs.trim() === '') {
          errors.epochs = 'Epochs is required';
        }
        if (!config.learningRate || config.learningRate.trim() === '') {
          errors.learningRate = 'Learning rate is required';
        }
        break;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderFormFields = () => {
    switch (algorithm) {
      case 'support vector':
        return (
          <>
            <div className="form-field">
              <label htmlFor="kernel">Kernel</label>
              <select
                id="kernel"
                value={config.kernel || 'rbf'}
                onChange={(e) => handleInputChange('kernel', e.target.value as 'rbf' | 'poly' | 'linear')}
              >
                <option value="rbf">Rbf</option>
                <option value="poly">Poly</option>
                <option value="linear">Linear</option>
              </select>
              <p className="field-description">Specifies the kernel type to be used to map input data into higher dimensions</p>
            </div>

            {config.kernel === 'poly' && (
              <div className="form-field">
                <label htmlFor="degree">Degree</label>
                <input
                  id="degree"
                  type="number"
                  value={config.degree}
                  onChange={(e) => handleInputChange('degree', e.target.value)}
                  placeholder="3"
                  className={fieldErrors.degree ? 'error' : ''}
                />
                {fieldErrors.degree && <p className="field-error">{fieldErrors.degree}</p>}
                <p className="field-description">Degree of polynomial kernel, must be non-negative. Ignored by other kernels</p>
              </div>
            )}

            <div className="form-field">
              <label htmlFor="c">C</label>
              <input
                id="c"
                type="number"
                value={config.c}
                onChange={(e) => handleInputChange('c', e.target.value)}
                placeholder="0"
                className={fieldErrors.c ? 'error' : ''}
              />
              {fieldErrors.c && <p className="field-error">{fieldErrors.c}</p>}
              <p className="field-description">Regularization parameter to prevent overfitting. The strength of regularization is inversely proportional to C. Must be positive.</p>
            </div>

            <div className="form-field">
              <label htmlFor="testTrainSplit">Test-train data split</label>
              <div className="input-with-suffix">
                <input
                  id="testTrainSplit"
                  type="number"
                  value={config.testTrainSplit}
                  onChange={(e) => handleInputChange('testTrainSplit', e.target.value)}
                  placeholder="0"
                  className={fieldErrors.testTrainSplit ? 'error' : ''}
                />
                <span className="input-suffix">%</span>
              </div>
              {fieldErrors.testTrainSplit && <p className="field-error">{fieldErrors.testTrainSplit}</p>}
              <p className="field-description">Percentage of dataset to use for testing</p>
            </div>

            <div className="form-field">
              <label htmlFor="scaler">Scaler</label>
              <select
                id="scaler"
                value={config.scaler}
                onChange={(e) => handleInputChange('scaler', e.target.value as 'standard scaler' | 'min-max scaler' | 'none')}
              >
                <option value="standard scaler">Standard Scaler</option>
                <option value="min-max scaler">Min-max Scaler</option>
                <option value="none">None</option>
              </select>
              <p className="field-description">Used to scale the input data</p>
            </div>
          </>
        );

      case 'random forest':
        return (
          <>
            <div className="form-field">
              <label htmlFor="nEstimators">N Estimators</label>
              <input
                id="nEstimators"
                type="number"
                value={config.nEstimators ?? '10'}
                onChange={(e) => handleInputChange('nEstimators', e.target.value)}
                placeholder="0"
                className={fieldErrors.nEstimators ? 'error' : ''}
              />
              {fieldErrors.nEstimators && <p className="field-error">{fieldErrors.nEstimators}</p>}
              <p className="field-description">The number of decision trees in the emodel</p>
            </div>

            <div className="form-field">
              <label htmlFor="maxDepth">Max Depth</label>
              <input
                id="maxDepth"
                type="number"
                value={config.maxDepth ?? '10'}
                onChange={(e) => handleInputChange('maxDepth', e.target.value)}
                placeholder="0"
                className={fieldErrors.maxDepth ? 'error' : ''}
              />
              {fieldErrors.maxDepth && <p className="field-error">{fieldErrors.maxDepth}</p>}
              <p className="field-description">Maximum depth of each tree</p>
            </div>

            <div className="form-field">
              <label htmlFor="testTrainSplit">Test-train data split</label>
              <div className="input-with-suffix">
                <input
                  id="testTrainSplit"
                  type="number"
                  value={config.testTrainSplit}
                  onChange={(e) => handleInputChange('testTrainSplit', e.target.value)}
                  placeholder="0"
                  className={fieldErrors.testTrainSplit ? 'error' : ''}
                />
                <span className="input-suffix">%</span>
              </div>
              {fieldErrors.testTrainSplit && <p className="field-error">{fieldErrors.testTrainSplit}</p>}
              <p className="field-description">Percentage of dataset to use for testing</p>
            </div>

            <div className="form-field">
              <label htmlFor="scaler">Scaler</label>
              <select
                id="scaler"
                value={config.scaler}
                onChange={(e) => handleInputChange('scaler', e.target.value as 'standard scaler' | 'min-max scaler' | 'none')}
              >
                <option value="standard scaler">Standard Scaler</option>
                <option value="min-max scaler">Min-max Scaler</option>
                <option value="none">None</option>
              </select>
              <p className="field-description">Used to scale the input data</p>
            </div>
          </>
        );

      case 'neural network':
        return (
          <>
            <div className="form-field">
              <label htmlFor="numLayers">Number of layers</label>
              <input
                id="numLayers"
                type="number"
                value={config.numLayers ?? '10'}
                onChange={(e) => handleInputChange('numLayers', e.target.value)}
                placeholder="10"
                className={fieldErrors.numLayers ? 'error' : ''}
              />
              {fieldErrors.numLayers && <p className="field-error">{fieldErrors.numLayers}</p>}
              <p className="field-description">Number of layers in the neural network model</p>
            </div>

            <div className="form-field">
              <label htmlFor="numNodes">Number of nodes</label>
              <input
                id="numNodes"
                type="number"
                value={config.numNodes ?? '512'}
                onChange={(e) => handleInputChange('numNodes', e.target.value)}
                placeholder="512"
                className={fieldErrors.numNodes ? 'error' : ''}
              />
              {fieldErrors.numNodes && <p className="field-error">{fieldErrors.numNodes}</p>}
              <p className="field-description">Number of nodes in each layer</p>
            </div>

            <div className="form-field">
              <label htmlFor="epochs">Epochs</label>
              <input
                id="epochs"
                type="number"
                value={config.epochs ?? '100'}
                onChange={(e) => handleInputChange('epochs', e.target.value)}
                placeholder="100"
                className={fieldErrors.epochs ? 'error' : ''}
              />
              {fieldErrors.epochs && <p className="field-error">{fieldErrors.epochs}</p>}
              <p className="field-description">Number of iterations to train the model for</p>
            </div>

            <div className="form-field">
              <label htmlFor="learningRate">Learning rate</label>
              <input
                id="learningRate"
                type="number"
                step="0.0001"
                value={config.learningRate ?? '0.0001'}
                onChange={(e) => handleInputChange('learningRate', e.target.value)}
                placeholder="0.0001"
                className={fieldErrors.learningRate ? 'error' : ''}
              />
              {fieldErrors.learningRate && <p className="field-error">{fieldErrors.learningRate}</p>}
              <p className="field-description">Step size for updating model parameters at each epoch</p>
            </div>

            <div className="form-field">
              <label htmlFor="testTrainSplit">Test-train data split</label>
              <div className="input-with-suffix">
                <input
                  id="testTrainSplit"
                  type="number"
                  value={config.testTrainSplit}
                  onChange={(e) => handleInputChange('testTrainSplit', e.target.value)}
                  placeholder="0"
                  className={fieldErrors.testTrainSplit ? 'error' : ''}
                />
                <span className="input-suffix">%</span>
              </div>
              {fieldErrors.testTrainSplit && <p className="field-error">{fieldErrors.testTrainSplit}</p>}
              <p className="field-description">Percentage of dataset to use for testing</p>
            </div>

            <div className="form-field">
              <label htmlFor="scaler">Scaler</label>
              <select
                id="scaler"
                value={config.scaler}
                onChange={(e) => handleInputChange('scaler', e.target.value as 'standard scaler' | 'min-max scaler' | 'none')}
              >
                <option value="standard scaler">Standard Scaler</option>
                <option value="min-max scaler">Min-max Scaler</option>
                <option value="none">None</option>
              </select>
              <p className="field-description">Used to scale the input data</p>
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="form-field">
              <label htmlFor="testTrainSplit">Test-train data split</label>
              <div className="input-with-suffix">
                <input
                  id="testTrainSplit"
                  type="number"
                  value={config.testTrainSplit}
                  onChange={(e) => handleInputChange('testTrainSplit', e.target.value)}
                  placeholder="0"
                  className={fieldErrors.testTrainSplit ? 'error' : ''}
                />
                <span className="input-suffix">%</span>
              </div>
              {fieldErrors.testTrainSplit && <p className="field-error">{fieldErrors.testTrainSplit}</p>}
              <p className="field-description">Percentage of dataset to use for testing</p>
            </div>

            <div className="form-field">
              <label htmlFor="scaler">Scaler</label>
              <select
                id="scaler"
                value={config.scaler}
                onChange={(e) => handleInputChange('scaler', e.target.value as 'standard scaler' | 'min-max scaler' | 'none')}
              >
                <option value="standard scaler">Standard Scaler</option>
                <option value="min-max scaler">Min-max Scaler</option>
                <option value="none">None</option>
              </select>
              <p className="field-description">Used to scale the input data</p>
            </div>
          </>
        );
    }
  };

  const handleContinue = () => {
    if (!saiFileId) {
      setError('SAI file ID is missing');
      return;
    }

    if (!validateConfig()) {
      setError('Please fill in all required fields');
      return;
    }

    setError(null);
    onNext({ config });
  };

  const isFormValid = (): boolean => {
    // Quick check without setting errors
    if (!config.testTrainSplit || config.testTrainSplit.trim() === '') {
      return false;
    }

    switch (algorithm) {
      case 'support vector':
        if (!config.c || config.c.trim() === '') return false;
        if (config.kernel === 'poly' && (!config.degree || config.degree.trim() === '')) return false;
        break;
      case 'random forest':
      case 'gradient boosting':
        if (!config.nEstimators || config.nEstimators.trim() === '') return false;
        if (!config.maxDepth || config.maxDepth.trim() === '') return false;
        break;
      case 'neural network':
        if (!config.numLayers || config.numLayers.trim() === '') return false;
        if (!config.numNodes || config.numNodes.trim() === '') return false;
        if (!config.epochs || config.epochs.trim() === '') return false;
        if (!config.learningRate || config.learningRate.trim() === '') return false;
        break;
    }

    return true;
  };

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Configure {getAlgorithmTitle()} Parameters</h1>
      </div>

      <p className="ml-config-description">
        Configure the hyperparameters for your selected {getAlgorithmTitle()} algorithm.
      </p>

      <div className="ml-config-layout">
        <div className="ml-config-form">
          {renderFormFields()}
        </div>

        <div className="ml-config-info">
          <div className="info-icon">
            <div className="info-icon-circle">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <g clip-path="url(#clip0_259_626)">
                  <path d="M8.00016 10.6667V8.00004M8.00016 5.33337H8.00683M14.6668 8.00004C14.6668 11.6819 11.6821 14.6667 8.00016 14.6667C4.31826 14.6667 1.3335 11.6819 1.3335 8.00004C1.3335 4.31814 4.31826 1.33337 8.00016 1.33337C11.6821 1.33337 14.6668 4.31814 14.6668 8.00004Z" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_259_626">
                    <rect width="16" height="16" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>

          <div className="info-content">
            {infoItems.map((item, index) => (
              <div key={index} className="info-item">
                <h4>{item.title}</h4>
                <p>{item.description}</p>
                {item.subItems && renderSubItems(item.subItems)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem' }}>
          {error}
        </div>
      )}

      <button
        className="btn btn-primary continue-btn-full"
        onClick={handleContinue}
        disabled={!saiFileId || !isFormValid()}
      >
        Next: Train Algorithm
        <span className="arrow-right">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
    </div>
  );
};

